import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useMediaQuery, useTheme } from '@material-ui/core';

import { aggregatedSurveyResponsesForSurveyIdSelector } from './survey-response-selector';
import {
  employeesForGroupIdSelector,
  employeesForOrgIdSelector,
} from '../../reducers/employee-selectors';
import {
  surveyForIdSelector,
  surveyResponsesStateForSurveyIdSelector,
} from '../../reducers/survey-selectors';
import { loadSurveyResponses } from '../actions/survey/load-survey-responses';
import { loadSurveys } from '../actions/survey/load-surveys';
import { sendSurveyReminder } from '../actions/survey/send-survey-reminder';

import { FeatureFlagged, FeatureGatedVariant } from '../../feature-gates/feature-flagged';
import { FeatureGates } from '../../feature-gates/feature-gates';
import { OnMessageSendInput } from '../../typescript-types/messages/on-message-send-type';
import { UiDialog } from '../../ui/ui-dialog';

import { DEFAULT_SURVEY_REMINDER_MESSAGES } from './utils/default-survey-reminder-messages';
import { LanguageCode } from '../../util/languages';
import { SURVEY_TYPE_REMINDER_LIMITS } from '../../util/survey-type-reminder-limits';
import { LegacyMultiLanguageMessageComposer } from '../../widgets/message-composer/legacy-multiple-language-message-composer';
import { MultiLanguageMessageComposer } from '../../widgets/message-composer/multiple-language-message-composer';
import { employeeLanguages } from '../utils/employee-languages';

const mapStateToProps = (state: any, { orgId, surveyId }: InputProps) => ({
  survey: surveyForIdSelector(state, surveyId),
  surveyResponses: aggregatedSurveyResponsesForSurveyIdSelector(state, surveyId),
  surveyResponsesState: surveyResponsesStateForSurveyIdSelector(state, surveyId),
  employeesForGroupId: (groupId: number) => employeesForGroupIdSelector(state, groupId),
  employeesForOrg: employeesForOrgIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId, surveyId }: InputProps,
) => ({
  actions: {
    loadSurveys: () => dispatch(loadSurveys(orgId)),
    loadSurveyResponses: () => dispatch(loadSurveyResponses(surveyId)),
    sendReminder: (message: Record<LanguageCode, string>, subject: Record<LanguageCode, string>) =>
      dispatch(sendSurveyReminder(surveyId, message, subject)),
  },
});

type InputProps = {
  orgId: number;
  surveyId: number;
  onClose: () => void;
  surveyType: string;
};
type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const SendReminderDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    actions,
    survey,
    surveyResponses,
    onClose,
    employeesForOrg,
    employeesForGroupId,
    surveyType,
  } = props;

  const [reminderMessage, setReminderMessage] = useState<Record<LanguageCode, string> | null>(null);

  useEffect(() => {
    actions.loadSurveys();
    actions.loadSurveyResponses();
  });

  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  if (!survey || !surveyResponses) {
    return null;
  }

  const handleSendMessage = async (onMessageSendInput: OnMessageSendInput) =>
    actions.sendReminder(
      onMessageSendInput.languageToMessage,
      onMessageSendInput.languageToSubject,
    );

  let employeeRecipients: Employee[] | null = null;
  if (survey.EmployeeRecipients.length > 0) {
    employeeRecipients = survey.EmployeeRecipients;
  }
  if (survey.EmployeeGroupRecipients.length > 0) {
    // This assumes that we only send to one group at a time. If we change that in the future this logic will need
    // to change
    const [employeeGroup] = survey.EmployeeGroupRecipients;
    employeeRecipients = employeesForGroupId(Number(employeeGroup.employeeGroupId));
  }
  if (!employeeRecipients) {
    employeeRecipients = employeesForOrg;
  }

  employeeRecipients = employeeRecipients.filter((e) => e.isCurrentEmployee);

  const { languageCounts, defaultLanguage, languagesToSupport } =
    employeeLanguages(employeeRecipients);

  const initialReminderMessage: Record<LanguageCode, string> = {};
  languagesToSupport.forEach((language) => {
    if (language === 'en') {
      initialReminderMessage.en = survey.isAnonymous
        ? DEFAULT_SURVEY_REMINDER_MESSAGES.ANONYMOUS.en
        : DEFAULT_SURVEY_REMINDER_MESSAGES.NON_ANONYMOUS.en;
    } else if (language === 'es') {
      initialReminderMessage.es = survey.isAnonymous
        ? DEFAULT_SURVEY_REMINDER_MESSAGES.ANONYMOUS.es
        : DEFAULT_SURVEY_REMINDER_MESSAGES.NON_ANONYMOUS.es;
    }
  });

  if (!reminderMessage && Object.keys(initialReminderMessage).length > 0) {
    setReminderMessage(initialReminderMessage);
  }

  return (
    <UiDialog size="xs" onClose={onClose} title="Send Reminders">
      <UiDialog.Content>
        <div className="u-margin-y-sm">
          Please enter a message to send to employees who received the survey in the last{' '}
          {SURVEY_TYPE_REMINDER_LIMITS[surveyType.toUpperCase()]} days and haven&apos;t responded.
        </div>
        <FeatureFlagged
          variant={FeatureGatedVariant.HIDE_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <LegacyMultiLanguageMessageComposer
            enableText
            enableEmail={false}
            onSendMessage={handleSendMessage}
            onMessageSent={onClose}
            onChange={setReminderMessage}
            buttonClass="u-float-right"
            languageToMessage={reminderMessage}
            defaultLanguage={defaultLanguage}
            languagesToSupport={languagesToSupport}
            languageCounts={languageCounts}
            messagePreviewEmployees={employeeRecipients}
            messagePreviewType="survey"
            canPersonalize={!survey.isAnonymous}
          />
        </FeatureFlagged>
        <FeatureFlagged
          variant={FeatureGatedVariant.SHOW_ON_FLAG}
          flagName={FeatureGates.INTEGRATED_MESSAGING}
        >
          <MultiLanguageMessageComposer
            enableText
            enableEmail={false}
            onSendMessage={handleSendMessage}
            onMessageSent={onClose}
            onChange={setReminderMessage}
            buttonClass="u-float-right"
            languageToMessage={reminderMessage}
            defaultLanguage={defaultLanguage}
            languagesToSupport={languagesToSupport}
            languageCounts={languageCounts}
            messagePreviewEmployees={employeeRecipients}
            messagePreviewType="survey"
            canPersonalize={!survey.isAnonymous}
            isOnMobile={isOnMobile}
          />
        </FeatureFlagged>
      </UiDialog.Content>
    </UiDialog>
  );
});
